import { useEffect, useRef, useState } from 'react'
import { useAsistenciaApi } from './api'
import { t, mesLabel, Lang } from './i18n'
import styles from './FichaOverlay.module.css'
import dialogStyles from '../styles/Dialog.module.css'
import { RolSelector, Grupo } from './roles'
import { guardarFichero } from './descargar'

function progressClass(pct: number): string {
  if (pct >= 80) return styles.progressSuccess
  if (pct >= 60) return styles.progressWarning
  return styles.progressDanger
}

// Mismos umbrales que progressClass, pero para colorear el texto de "Total %" en vez de una barra.
function pctStatClass(pct: number | '--'): string | undefined {
  if (pct === '--') return undefined
  if (pct >= 80) return styles.statSuccess
  if (pct >= 60) return styles.statWarning
  return styles.statDanger
}

interface Ficha {
  persona: { nombre: string; alias: string | null; foto_url: string | null }
  grupo: string
  fecha_incorporacion: string
  estadisticas: {
    porcentaje_total: number | '--'
    presencias: number
    faltas: number
    sesiones: number
  }
  contadores?: Record<string, number>
  desglose_mensual: { mes: string; sesiones: number; presencias: number; porcentaje: number | '--' }[]
}

export function FichaOverlay({
  equipoId,
  miembroId,
  lang,
  rolEntrenadorInicial,
  onClose,
  onChanged,
}: {
  equipoId: string
  miembroId: string
  lang: Lang
  rolEntrenadorInicial?: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const api = useAsistenciaApi()
  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [nombre, setNombre] = useState('')
  const [rolEntrenador, setRolEntrenador] = useState(rolEntrenadorInicial ?? '')
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function refresh() {
    const data: Ficha = await api.get(`/miembros/${miembroId}/ficha?equipo_id=${equipoId}`)
    setFicha(data)
    setNombre(data.persona.nombre)
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miembroId])

  // Issue #80: cambio de rol desde la ficha (misma persona, sin borrarla ni volver a crearla).
  async function handleCambiarRol(grupo: Grupo) {
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, { grupo })
    await refresh()
    onChanged()
  }

  async function handleGuardarNombre() {
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, { nombre })
    await refresh()
    onChanged()
  }

  async function handleGuardarRolEntrenador() {
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, { rol_entrenador: rolEntrenador })
    onChanged()
  }

  async function handleSubirFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    await api.postForm(`/miembros/${miembroId}/foto`, formData, 'PATCH')
    await refresh()
    onChanged()
  }

  async function handleEliminarJugador() {
    if (!window.confirm(t('eliminarJugadorConfirm', lang))) return
    await api.patch(`/equipos/${equipoId}/miembros/${miembroId}`, {
      fecha_baja: new Date().toISOString().slice(0, 10),
    })
    onChanged()
    onClose()
  }

  if (!ficha) return null

  return (
    <dialog open onClose={onClose} className={dialogStyles.dialog}>
      <h3>{t('fichaJugador', lang)}</h3>

      {ficha.persona.foto_url ? (
        <img className={styles.avatar} src={ficha.persona.foto_url} alt="" width={88} height={88} />
      ) : (
        <span aria-hidden className={styles.avatarFallback}>
          {ficha.persona.nombre.slice(0, 2).toUpperCase()}
        </span>
      )}
      <button onClick={() => fileInputRef.current?.click()}>{t('subirFoto', lang)}</button>
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleSubirFoto} />

      <label>
        {t('nombre', lang)}
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </label>
      <button onClick={handleGuardarNombre}>{t('guardar', lang)}</button>
      <RolSelector grupo={ficha.grupo as Grupo} lang={lang} onChange={handleCambiarRol} />

      {ficha.grupo === 'entrenador' && (
        <>
          <label>
            {t('rolEntrenador', lang)}
            <input value={rolEntrenador} onChange={(e) => setRolEntrenador(e.target.value)} />
          </label>
          <button onClick={handleGuardarRolEntrenador}>{t('guardar', lang)}</button>
        </>
      )}

      <dl className={styles.statsGrid}>
        <dt>{t('totalPorc', lang)}</dt>
        <dd className={pctStatClass(ficha.estadisticas.porcentaje_total)}>{ficha.estadisticas.porcentaje_total}</dd>
        <dt>{t('presencias', lang)}</dt>
        <dd className={styles.statSuccess}>{ficha.estadisticas.presencias}</dd>
        <dt>{t('faltas', lang)}</dt>
        <dd className={styles.statDanger}>{ficha.estadisticas.faltas}</dd>
        <dt>{t('sesiones', lang)}</dt>
        <dd>{ficha.estadisticas.sesiones}</dd>
        {/* Club por WhatsApp, 2026-10-13: motivo de las faltas (Eskola/F7) — "sin motivo" en rojo,
            para detectar a tiempo a quien deja de venir sin avisar. */}
        {ficha.grupo !== 'entrenador' && (
          <>
            <dt>{t('faltaEN', lang)}</dt>
            <dd>{ficha.contadores?.EN ?? 0}</dd>
            <dt>{t('faltaLS', lang)}</dt>
            <dd>{ficha.contadores?.LS ?? 0}</dd>
            <dt>{t('faltaJU', lang)}</dt>
            <dd>{ficha.contadores?.JU ?? 0}</dd>
            <dt>{t('faltaNJ', lang)}</dt>
            <dd className={(ficha.contadores?.NJ ?? 0) > 0 ? styles.statDanger : undefined}>{ficha.contadores?.NJ ?? 0}</dd>
          </>
        )}
      </dl>

      <table>
        <thead>
          <tr>
            <th>{t('sesiones', lang)}</th>
            <th>{t('presencias', lang)}</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {ficha.desglose_mensual.map((fila) => (
            <tr key={fila.mes}>
              <td>{mesLabel(fila.mes, lang)}</td>
              <td>{fila.presencias}</td>
              <td>
                {fila.porcentaje}
                {typeof fila.porcentaje === 'number' && (
                  <progress className={progressClass(fila.porcentaje)} value={fila.porcentaje} max={100} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="modal-actions">
        {/* Issue #76: Excel individual (datos, historial de asistencia y %). */}
        <button onClick={() => guardarFichero(api.downloadFile(`/miembros/${miembroId}/exportar?equipo_id=${equipoId}&lang=${lang}`))}>
          {t('excelJugador', lang)}
        </button>
        <button onClick={handleEliminarJugador}>{t('eliminarJugador', lang)}</button>
        <button onClick={onClose}>{t('cerrar', lang)}</button>
      </div>
    </dialog>
  )
}
